import React, { useState, useEffect } from 'react';
import { 
    CreditCard, Clock, FileText, Download, Upload, 
    AlertTriangle, CheckCircle2, History, LogOut,
    LayoutDashboard, HelpCircle, ChevronDown, Filter
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getClientProfile, getClientTransactions, getInvoiceBlobUrl } from '../../firebase/clientService';
import PaymentModal from './PaymentModal';
import { TransaccionStatus } from '../movimientos/TransaccionStatus';

const SubscriptionPanel = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [clientData, setClientData] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

    const fetchData = async () => {
        try {
            const [profile, txs] = await Promise.all([
                getClientProfile(),
                getClientTransactions()
            ]);
            setClientData(profile);
            setTransactions(txs || []);
        } catch (error) {
            console.error("Error fetching data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === 'Admin') {
            navigate('/admin');
            return;
        }
        fetchData();
    }, [user, navigate]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    // Data mapping from SQL or Fallback
    const client = clientData ? {
        name: clientData.CompanyName || user?.company || "Empresa",
        subscriptions: clientData.subscriptions || [],
        status: clientData.Status || "active",
        amountDue: clientData.AmountDue ? `$ ${clientData.AmountDue.toLocaleString('es-AR')}` : "$ 0,00"
    } : {
        name: user?.company || "Cargando...",
        subscriptions: [],
        status: "active",
        amountDue: "-"
    };

    const credits = clientData ? {
        total: clientData.CreditosTotales || 0,
        used: clientData.CreditosUsados || 0,
        available: (clientData.CreditosTotales || 0) - (clientData.CreditosUsados || 0),
        resetDate: "01 Mes Siguiente"
    } : {
        total: 0,
        used: 0,
        available: 0,
        resetDate: "-"
    };

    const handleExportCSV = () => {
        const headers = ["Fecha", "Descripción", "Monto", "Tipo", "Estado", "Comprobante"];
        const rows = transactions.map(t => [
            t.date, 
            t.desc, 
            t.amount, 
            t.type, 
            t.status, 
            t.invoice
        ]);
        
        const csvContent = [
            headers.join(","),
            ...rows.map(r => r.join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "cuenta_corriente_regidoft.csv");
        document.body.appendChild(link);
        link.click();
    };

    const handleViewInvoice = async (transactionId) => {
        try {
            const url = await getInvoiceBlobUrl(transactionId);
            window.open(url, '_blank');
        } catch (error) {
            console.error("Error viewing invoice:", error);
            alert("No se pudo abrir el comprobante.");
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-700">
            
            {/* Top Navigation Bar */}
            <nav className="bg-slate-900 text-white px-6 py-4 flex justify-between items-center shadow-md">
                <div className="flex items-center gap-3">
                    <img src="/xp_dark.png" alt="X Project" className="h-6" />
                    <h1 className="font-bold text-lg leading-tight ps-3">Gestión de Suscripción</h1>
                </div>
                <div className="flex items-center gap-6">
                    <div className="hidden md:block text-right">
                        <p className="text-sm font-bold">{client.name}</p>
                        <p className="text-xs text-slate-400">
                            {client.subscriptions.length} suscripci{client.subscriptions.length !== 1 ? 'ones' : 'ón'} activa{client.subscriptions.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <button onClick={handleLogout} className="p-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-white">
                        <LogOut size={20} />
                    </button>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto p-6 space-y-8">
                
                {/* Alert Banner */}
                {client.status === 'warning' && (
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg shadow-sm flex items-start gap-4 animate-fade-in-down">
                        <div className="p-2 bg-amber-100 rounded-full text-amber-600 shrink-0">
                            <AlertTriangle size={24} />
                        </div>
                        <div className="flex-grow">
                            <h3 className="font-bold text-amber-800 text-lg">Atención: Suscripción próxima a vencer</h3>
                            <p className="text-amber-700">
                                Su factura del período actual vence pronto y aún no registra pago. 
                                Por favor regularice su situación para evitar la suspensión del servicio.
                            </p>
                        </div>
                        <button className="px-4 py-2 bg-amber-600 text-white font-bold rounded-lg hover:bg-amber-700 transition-colors shadow-sm">
                            Informar Pago
                        </button>
                    </div>
                )}

                {/* KPI Cards */}
                <div className="grid md:grid-cols-3 gap-6">
                    
                    {/* Credits Card */}
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -mr-10 -mt-10 z-0"></div>
                        <div className="relative z-10 h-full flex flex-col">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                                    <Clock size={24} />
                                </div>
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Mensual</span>
                            </div>
                            <h3 className="text-slate-500 font-medium mb-3">Créditos de Horas</h3>
                            
                            {client.subscriptions.length > 0 ? (
                                <div className="space-y-4 flex-grow pr-2">
                                    {client.subscriptions.map((sub, idx) => (
                                        <div key={idx} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-sm font-bold text-slate-700">{sub.product}</span>
                                                <span className="text-xs text-slate-500">{sub.plan}</span>
                                            </div>
                                            <div className="flex items-baseline gap-2 mb-1">
                                                <span className="text-lg font-extrabold text-slate-900">{(sub.CreditosTotales || 0) - (sub.CreditosUsados || 0)}h</span>
                                                <span className="text-xs text-slate-400">/ {sub.CreditosTotales || 0}h</span>
                                            </div>
                                            <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                                <div 
                                                    className="bg-blue-600 h-full rounded-full" 
                                                    style={{ width: `${sub.CreditosTotales > 0 ? ((sub.CreditosUsados || 0) / sub.CreditosTotales) * 100 : 0}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex-grow flex items-center justify-center text-slate-400 text-sm">
                                    Sin créditos activos
                                </div>
                            )}
                            
                            <p className="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100">
                                Se renuevan mensualmente.
                            </p>
                        </div>
                    </div>

                    {/* Balance Card */}
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-full -mr-10 -mt-10 z-0"></div>
                        <div className="relative z-10 flex flex-col h-full">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-green-50 text-green-600 rounded-lg">
                                    <CreditCard size={24} />
                                </div>
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Estado</span>
                            </div>
                            <h3 className="text-slate-500 font-medium mb-3">Saldo Pendiente</h3>
                            
                            {client.subscriptions.length > 0 ? (
                                <div className="space-y-4 flex-grow pr-2 mb-4">
                                    {client.subscriptions.map((sub, idx) => (
                                        <div key={idx} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-sm font-bold text-slate-700">{sub.product}</span>
                                                <span className="text-xs text-slate-500">{sub.plan}</span>
                                            </div>
                                            <div className="flex items-baseline gap-2">
                                                <span className={`text-xl font-extrabold ${sub.SaldoPendiente > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                    $ {sub.SaldoPendiente ? sub.SaldoPendiente.toLocaleString('es-AR') : '0,00'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex-grow flex items-center justify-center text-slate-400 text-sm mb-4">
                                    Sin saldos pendientes
                                </div>
                            )}

                            <div className="flex gap-2 mt-auto">
                                <button 
                                    onClick={() => setIsPaymentModalOpen(true)}
                                    className="flex-1 py-2 bg-slate-900 text-white text-sm font-bold rounded-lg hover:bg-slate-800 transition-colors"
                                >
                                    Pagar Ahora
                                </button>
                                <button 
                                    onClick={() => setIsPaymentModalOpen(true)}
                                    className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600"
                                >
                                    <Upload size={20} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Plan Info - Ahora es un carrusel de suscripciones */}
                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 rounded-xl shadow-lg text-white flex flex-col relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -mr-10 -mt-10"></div>
                        <div className="relative z-10 h-full flex flex-col">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-white/10 rounded-lg">
                                    <LayoutDashboard size={24} />
                                </div>
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded text-xs font-bold uppercase">
                                    Activo
                                </span>
                            </div>
                            
                            {client.subscriptions.length > 0 ? (
                                <div className="flex-grow">
                                    <h3 className="text-slate-400 font-medium mb-1">Suscripciones</h3>
                                    <div className="space-y-4 mt-2">
                                        {client.subscriptions.map((sub, idx) => (
                                            <div key={idx} className="border-b border-white/10 pb-2 last:border-0 last:pb-0">
                                                <p className="text-lg font-bold">{sub.product}</p>
                                                <p className="text-sm text-slate-300">{sub.plan}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-grow flex items-center justify-center">
                                    <p className="text-slate-400">No hay suscripciones activas</p>
                                </div>
                            )}

                            <button className="mt-6 w-full py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-bold rounded-lg transition-colors border border-white/10">
                                Gestionar Planes
                            </button>
                        </div>
                    </div>
                </div>

                {/* Current Account Table */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">Cuenta Corriente</h2>
                            <p className="text-slate-500 text-sm">Historial de movimientos, facturas y pagos.</p>
                        </div>
                        <div className="flex gap-3">
                            <button 
                                onClick={() => setIsPaymentModalOpen(true)}
                                className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 font-bold text-sm rounded-lg hover:bg-slate-50 transition-colors"
                            >
                                <Upload size={16} />
                                Subir Comprobante
                            </button>
                            <button 
                                onClick={handleExportCSV}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-bold text-sm rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                            >
                                <Download size={16} />
                                Exportar CSV
                            </button>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4">Fecha</th>
                                    <th className="px-6 py-4">Descripción</th>
                                    <th className="px-6 py-4">Comprobante</th>
                                    <th className="px-6 py-4 text-right">Monto</th>
                                    <th className="px-6 py-4 text-center">Estado</th>
                                    <th className="px-6 py-4 text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {transactions.map((t) => (
                                    <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-slate-900">{t.date}</td>
                                        <td className="px-6 py-4 text-slate-600">
                                            <div className="flex items-center gap-2">
                                                {t.type === 'credit' ? (
                                                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                                ) : (
                                                    <span className="w-2 h-2 rounded-full bg-slate-300"></span>
                                                )}
                                                {t.desc}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-mono text-xs text-slate-500">{t.invoice}</td>
                                        <td className={`px-6 py-4 text-right font-bold ${t.type === 'credit' ? 'text-green-600' : 'text-slate-900'}`}>
                                            {t.type === 'credit' ? '+' : ''} $ {t.amount.toLocaleString('es-AR')}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <TransaccionStatus status={t.status} />
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button 
                                                onClick={() => handleViewInvoice(t.id)}
                                                className="text-blue-600 hover:text-blue-800 font-bold text-xs flex items-center justify-center gap-1 mx-auto"
                                            >
                                                <FileText size={14} />
                                                Ver
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="p-4 border-t border-slate-200 bg-slate-50 text-center text-xs text-slate-400">
                        Mostrando últimos 4 movimientos. <a href="#" className="text-blue-600 hover:underline">Ver historial completo</a>
                    </div>
                </div>

            </main>

            <PaymentModal 
                isOpen={isPaymentModalOpen} 
                onClose={() => setIsPaymentModalOpen(false)}
                subscriptions={client.subscriptions}
                onSuccess={fetchData}
            />
        </div>
    );
};

export default SubscriptionPanel;